import { useTheme } from "@emotion/react";
import { faFaceDotted } from "@fortawesome/pro-light-svg-icons";
import type { IconDefinition } from "@fortawesome/pro-solid-svg-icons";
import { useQuery } from "@tanstack/react-query";
import type {
    IconLibrary,
    IconName,
    RemSize,
    Translatable} from "@towni/common";
import {
    YEAR,
} from "@towni/common";
import { useMainFetchClient } from "../fetch-client";
import { Icon } from "./icon";

type Props = {
    icon: IconName | undefined;
    spin?: boolean;
    fixedWidth?: boolean;
    className?: string;
    size?: number | RemSize;
    /** defaults to regular */
    library?: IconLibrary;
    title?: Translatable;
    fallback?: IconDefinition;
    transparentWhenNoIcon?: boolean;
};

const useDynamicIconQuery = (
    icon: IconName | undefined,
    library: IconLibrary | undefined,
) => {
    const fetchClient = useMainFetchClient();
    return useQuery<IconDefinition | undefined>({
        queryKey: ["fa", library, icon],
        queryFn: async ({ signal }) => {
            return await fetchClient.get<IconDefinition>({
                route: `/icons/dynamic/${library}/${icon}`,
                customConfig: { signal },
            });
        },
        enabled: !!icon && !!library,
        staleTime: 1 * YEAR,
    });
};

const DynamicIcon = (props: Props) => {
    const theme = useTheme();
    const library = props.library || "regular";
    const iconQuery = useDynamicIconQuery(props.icon, library);
    const icon = iconQuery.data || undefined;

    return (
        <Icon
            icon={props.transparentWhenNoIcon && !icon ? faFaceDotted : icon}
            fixedWidth={props.fixedWidth}
            size={props.size}
            spin={props.spin || iconQuery.isPending}
            className={props.className}
            delayRenderingInMs={!icon ? 50 : undefined}
            title={props.title}
            css={{
                color:
                    props.transparentWhenNoIcon && !icon
                        ? theme.colors.transparent.asString
                        : undefined,
            }}
        />
    );
};

export { DynamicIcon, useDynamicIconQuery };
export type { IconLibrary };
